/**
 * PAYMENT_SOURCES (AF-4126): um bloco por fonte, um embaixo do outro. O `alias` é a chave e não muda;
 * nome, aval a devolver e as naturezas em que a fonte é a padrão são editáveis. Cada natureza tem uma
 * fonte padrão só: marcar num bloco desmarca nos outros.
 */
import ACheckbox from '../../../atoms/ACheckbox';
import TextEdit from '../../../molecules/TextEdit';
import { NATURES, Nature, parseJson, PaymentSource, toggleDefaultIf } from '../services/businessPropertyRules';

interface Props {
    value: string;
    onChange: (value: string) => void;
    error: string | null;
}

const CARD = 'bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-4 shadow-sm';

const replaceSource = (sources: PaymentSource[], alias: string, changes: Partial<PaymentSource>) =>
    sources.map(source => (source.alias === alias ? { ...source, ...changes } : source));

const PaymentSourcesEditor = ({ value, onChange, error }: Props) => {
    const sources = parseJson<PaymentSource[]>(value, []);

    return (
        <div className="flex flex-col gap-3">
            {sources.map(source => (
                <section key={source.alias} aria-label={source.alias} className={CARD}>
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">{source.alias}</p>
                    <TextEdit
                        id={`payment-source-name-${source.alias}`}
                        label="Nome"
                        value={source.name}
                        onChange={e => onChange(JSON.stringify(replaceSource(sources, source.alias, { name: e.target.value })))}
                    />
                    <div className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-200">
                        <ACheckbox
                            ariaLabel="Permite aval a devolver"
                            checked={source.can_guarantee_refund}
                            onChange={checked => onChange(JSON.stringify(replaceSource(sources, source.alias, { can_guarantee_refund: checked })))}
                        />
                        <span>Permite aval a devolver</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-700 dark:text-gray-200">
                        <span>Padrão para:</span>
                        {NATURES.map((nature: Nature) => (
                            <div key={nature} className="flex items-center gap-2">
                                <ACheckbox
                                    ariaLabel={nature}
                                    checked={source.default_if.includes(nature)}
                                    onChange={checked => onChange(JSON.stringify(toggleDefaultIf(sources, source.alias, nature, checked)))}
                                />
                                <span>{nature}</span>
                            </div>
                        ))}
                    </div>
                </section>
            ))}
            {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
    );
};

export default PaymentSourcesEditor;
